import {
    AssetCriticalityType,
    AssetRunningSpeedType,
    AssetUpdateRequest,
    IAssetCategoryResponse,
    IAssetResponse,
    IAssetUpdateRequest,
    RunningSpeedUnit,
} from '@kcftech/prometheus-client';
import { sdClient } from 'contexts/SDKClientContext';
import { getRunningSpeedDisplayUnits } from 'utils/units';

export const criticalityOptions = [
    {
        title: 'Extreme',
        value: AssetCriticalityType.Extreme,
        description:
            'Extreme - Asset shuts down entire plant and would have immediate and long-lasting production loss',
    },
    {
        title: 'High',
        value: AssetCriticalityType.High,
        description:
            'High - Asset shuts down part of plant or threatens to slow plant production if not attended to immediately',
    },
    {
        title: 'Moderate',
        value: AssetCriticalityType.Moderate,
        description:
            ' Moderate (default) - Production is not immediately impacted but problem requires attention to prevent future production losses',
    },
    {
        title: 'Low',
        value: AssetCriticalityType.Low,
        description:
            'Low - Redundancy exists or asset is not essential to production process',
    },
];

export const getAssetDetailRenderValue = (value: string | undefined) =>
    value && value !== '' ? value : undefined;

export const getExpectedSpeed = (
    type: AssetRunningSpeedType,
    min: string | number | undefined,
    max: string | number | undefined,
    units?: RunningSpeedUnit,
) => {
    if (
        type === AssetRunningSpeedType.Invalid ||
        type === AssetRunningSpeedType.None ||
        type === AssetRunningSpeedType.IndicatorBased
    ) {
        return undefined;
    }

    if (!units) {
        throw new Error(
            'It is invalid to have a type other than None or Indicator Based and have undefined units',
        );
    }

    const runningSpeedDisplayUnit = getRunningSpeedDisplayUnits(units);

    switch (type) {
        case AssetRunningSpeedType.Fixed:
            return `Fixed ${min} ${runningSpeedDisplayUnit}`;
        case AssetRunningSpeedType.Ranged:
            return `Ranged ${min} - ${max} ${runningSpeedDisplayUnit}`;
        case AssetRunningSpeedType.AutoTrack:
            return `Auto-Track ${min} - ${max} ${runningSpeedDisplayUnit}`;
        default:
            return undefined;
    }
};

export const getCategoryMarkerString = (asset: IAssetResponse) => {
    const marker = asset.Category?.CategoryMarker;
    return `${marker?.CategoryMarkerName} (${marker?.CategoryMarkerCode})`;
};

/**
 * Updates an Asset given its id and the updates to make. Loads the
 * asset to get all current information and adds the updates. Helps prevent getting
 * out of sync with the api or not passing in fields and accidentally clearing them.
 * @param assetId The id of the asset to update
 * @param updateRequestTransformer The fields to update on the asset
 * @param includeAnalyticsModels (optional) Determines whether to include the Analytics Model with the asset
 */
export async function updateAsset(
    assetId: string,
    updateRequestTransformer: (request: IAssetUpdateRequest) => void,
): Promise<IAssetResponse> {
    const {
        Supplier,
        Manufacturer,
        ModelName,
        LocationNotes,
        Notes,
        Metadata,
        Type,
        Category,
        Size,
        RunningSpeed,
        Criticality,
        Name,
        Abbreviation,
        IsPaused,
        EquipmentNumber,
    } = await sdClient.getAsset({ assetId, includeAnalyticsModels: true });

    const requestData: IAssetUpdateRequest = {
        IsPaused,
        // The response does not include PrimaryImageFileId, so it is omitted
        // here. We ignore undefined for this prop when processing the Update
        // request. We should really follow PUT semantics if we're using it. For
        // now, though, this stays.
        RunningSpeed,
        Name,
        Abbreviation,
        Supplier,
        Manufacturer,
        ModelName,
        LocationNotes,
        Notes,
        GenericInput: Metadata,
        TypeId: Type?.TypeId,
        CategoryId: Category?.CategoryId,
        SizeId: Size?.SizeId,
        Criticality,
        EquipmentNumber,
    };

    updateRequestTransformer(requestData);

    requestData.Name = requestData.Name.trim();

    const request = new AssetUpdateRequest(requestData);

    return sdClient.updateAsset({ assetId, body: request });
}

export type GetChildCategoriesOptions = Readonly<{
    allCategories: IAssetCategoryResponse[];
    parentCategoryId: number | undefined;
    excludeCategory?: IAssetCategoryResponse;
}>;

export const getChildCategories = ({
    allCategories,
    parentCategoryId,
    excludeCategory,
}: GetChildCategoriesOptions) =>
    allCategories.filter(
        (cat) =>
            cat.ParentCategory?.AssetCategoryParentId === parentCategoryId &&
            cat.Id !== excludeCategory?.Id,
    );
